/**
 * Constants + pure helpers for the cart approve / payment-link flow
 * (CartPaymentLinkDialog). Mirrors the appointment payFromBridge flow but is
 * driven by the cart payment-sublimit preview (paid vs target, signed deltas,
 * and a "no-wallet" alternative) instead of a flat grand total.
 */

const EPSILON = 0.005;

// Screen states for the cart payment flow.
export const CART_SCREEN_STATE = {
  SELECT_PAYMENT_METHOD: 0,
  SELECT_SHARE_LINK_OPTIONS: 1,
  SUCCESS_WITH_GENERATED_LINK: 2,
  SUCCESS_WITHOUT_LINK: 3, // wallet-only debit, refund-only, or no-op approve
  ERROR_IN_APPROVE: 4,
};

// Payment method options, derived from the preview `sublimit` block (mirrors
// the appointment flow). useWallet sent to /approve = method !== RAZORPAY_ONLY.
export const CART_PAYMENT_METHOD = {
  WALLET_AND_RAZORPAY: 1, // partial wallet + Razorpay for the remainder
  RAZORPAY_ONLY: 2, // full amount via Razorpay (useWallet=false)
  WALLET_ONLY: 3, // full amount via wallet (synchronous, no link)
  WALLET_DISABLED: 4, // wallet option shown but disabled (wallet empty)
};

// "Rs 1,700" (Indian grouping), matching CartPaymentDetailsDialog's formatRs.
export const formatCurrency = (n) => {
  const num = Number(n || 0);
  return "Rs " + num.toLocaleString("en-IN");
};

/**
 * Whether approving the cart collects any new money (wallet and/or external).
 * Based on the signed delta (preview.newPayment) — NOT the sublimit, which is
 * the full target split. False means refund-only or no-op: the dialog approves
 * directly (useWallet=true) and skips method selection. True means there is a
 * collection, so the method screen (sublimit-driven) is shown.
 */
export const cartNeedsCollection = (preview) => {
  const np = preview?.newPayment?.deltaComposition;
  if (!np) return false;
  return (np.wallet || 0) > EPSILON || (np.external || 0) > EPSILON;
};

/**
 * Total NEW money the patient pays when approving — `newPayment.total` (the
 * collection delta), NOT `delta.total` (the net of collection minus refund,
 * which can be negative). Shown as the "Grand total" on the proceed-to-pay
 * screens so the figure matches what the patient is actually charged.
 */
export const getCartNewPaymentTotal = (preview) => {
  return preview?.newPayment?.deltaComposition?.total ?? 0;
};

/**
 * Refund the approve will trigger regardless of the chosen option (the
 * "rebalance" case where wallet rules shift the split). Returns the primary
 * refund deltaComposition or null. Shown as an info note on the method /
 * success screens.
 */
export const getCartRefund = (preview) => {
  const r = preview?.refund?.deltaComposition;
  if (!r) return null;
  if ((r.wallet || 0) <= EPSILON && (r.external || 0) <= EPSILON) return null;
  return r;
};

/**
 * The three payment fields the /approve verify guard actually reads —
 * `{grandTotal, useZylaWallet, useExternalWallet}`. The backend recomputes the
 * whole split server-side and only checks useZylaWallet/useExternalWallet
 * against its own recompute (epsilon 0.005); grandTotal is never read (sent for
 * parity). So the approve body carries only these three, sourced by useWallet:
 *
 *   useWallet=true  → the preview `sublimit` collection split (its own fields).
 *   useWallet=false → the "no-wallet alternative" NET Razorpay top-up
 *     (`noWalletAlternative.newPayment.deltaComposition` = {wallet:0,
 *     external:netExternal, total:netExternal}). When that alternative is ABSENT
 *     (plain Razorpay / no-wallet / empty-wallet carts) the `sublimit`
 *     projection already equals the server's useWallet=false recompute, so fall
 *     back to it. A refund-only alternative (external < 0, or newPayment absent)
 *     collapses to {0,0,0}.
 */
export const getCartPaymentDetails = (preview, useWallet) => {
  const sublimit = preview?.sublimit;
  const fromSublimit = {
    grandTotal: sublimit?.grandTotal ?? 0,
    useZylaWallet: sublimit?.useZylaWallet ?? 0,
    useExternalWallet: sublimit?.useExternalWallet ?? 0,
  };

  if (useWallet) return fromSublimit;

  const nwa = preview?.noWalletAlternative;
  if (!nwa) return fromSublimit;

  const np = nwa?.newPayment?.deltaComposition;
  const external = np?.external ?? 0;
  if (external < 0) {
    return { grandTotal: 0, useZylaWallet: 0, useExternalWallet: 0 };
  }
  return {
    grandTotal: np?.total ?? 0,
    useZylaWallet: np?.wallet ?? 0,
    useExternalWallet: external,
  };
};

/**
 * "Grand total" figure for the header, dependent on the currently selected
 * method. For RAZORPAY_ONLY in the rebalance case it is the alternative's net
 * top-up (e.g. Rs 10), not `newPayment.total` (the wallet-path collection, e.g.
 * Rs 130). Every other method returns the usual `getCartNewPaymentTotal`.
 */
export const getCartGrandTotalForMethod = (preview, paymentMethod) => {
  if (paymentMethod === CART_PAYMENT_METHOD.RAZORPAY_ONLY) {
    return getCartPaymentDetails(preview, false).grandTotal;
  }
  return getCartNewPaymentTotal(preview);
};
